import { useQuery } from '@tanstack/react-query';
import type {
  ArticleDTO,
  DeviceDTO,
  LibraryItem,
  OrderDTO,
  ProductDetail,
  ProductSummary,
  PublicConfig,
  SessionDTO,
  TicketDTO,
} from '@va/shared';
import { useAuth } from '../state/auth';
import { api } from './client';

export function useConfig() {
  return useQuery({ queryKey: ['config'], queryFn: () => api.get<PublicConfig>('/config'), staleTime: 5 * 60_000 });
}

export function useProducts(params: { category?: string; q?: string } = {}) {
  const { user } = useAuth();
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => !!v) as [string, string][]).toString();
  return useQuery({
    queryKey: ['products', user?.id ?? null, qs],
    queryFn: () => api.get<ProductSummary[]>(`/products${qs ? `?${qs}` : ''}`),
  });
}

export function useProduct(slug: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['product', user?.id ?? null, slug],
    queryFn: () => api.get<ProductDetail>(`/products/${encodeURIComponent(slug!)}`),
    enabled: !!slug,
  });
}

export function useLibrary() {
  const { user } = useAuth();
  return useQuery({ queryKey: ['library', user?.id], queryFn: () => api.get<LibraryItem[]>('/library'), enabled: !!user });
}

export function useOrders() {
  const { user } = useAuth();
  return useQuery({ queryKey: ['orders', user?.id], queryFn: () => api.get<OrderDTO[]>('/orders'), enabled: !!user });
}

export function useDevices(machineId?: string | null) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['devices', user?.id, machineId ?? null],
    queryFn: () => api.get<DeviceDTO[]>(`/devices${machineId ? `?machineId=${machineId}` : ''}`),
    enabled: !!user,
  });
}

export function useSessions() {
  const { user } = useAuth();
  return useQuery({ queryKey: ['sessions', user?.id], queryFn: () => api.get<SessionDTO[]>('/me/sessions'), enabled: !!user });
}

export function useArticles() {
  return useQuery({ queryKey: ['articles'], queryFn: () => api.get<ArticleDTO[]>('/articles'), staleTime: 5 * 60_000 });
}

export function useArticle(slug: string | undefined) {
  return useQuery({ queryKey: ['article', slug], queryFn: () => api.get<ArticleDTO>(`/articles/${slug}`), enabled: !!slug });
}

export function useTickets() {
  const { user } = useAuth();
  return useQuery({ queryKey: ['tickets', user?.id], queryFn: () => api.get<TicketDTO[]>('/support/tickets'), enabled: !!user });
}
